import type { ReactNode } from "react";
import {
  forwardRef,
  useState,
  useCallback,
  useRef,
  Children,
  cloneElement,
  isValidElement,
} from "react";
import classNames from "classnames/dedupe";
import { Text, type DOMProps } from "@simplybusiness/mobius";
import type { PromptInputTextareaProps } from "./PromptInputTextarea";
import { PromptInputTextarea } from "./PromptInputTextarea";
import "./PromptInput.css";

export type PromptInputElementType = HTMLFormElement;
export type PromptInputRef = React.Ref<PromptInputElementType>;

export interface PromptInputProps extends Omit<DOMProps, "ref"> {
  /**
   * The content of the prompt input, typically PromptInputTextarea and PromptInputSubmit
   */
  children: ReactNode;
  /**
   * Callback when the form is submitted
   */
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  /**
   * Custom class name
   */
  className?: string;
  /**
   * Show the "This experience uses AI. BETA" disclaimer below the input
   */
  showDisclaimer?: boolean;
  /**
   * Disable the expand/collapse animation on focus
   */
  noAnimation?: boolean;
}

const PromptInput = forwardRef<PromptInputElementType, PromptInputProps>(
  (props: PromptInputProps, ref: PromptInputRef) => {
    const {
      children,
      onSubmit,
      className,
      showDisclaimer,
      noAnimation,
      ...otherProps
    } = props;
    const [isExpanded, setIsExpanded] = useState(false);
    const formRef = useRef<HTMLFormElement>(null);

    const handleSubmit = useCallback(
      (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        onSubmit?.(event);
        setIsExpanded(false);
      },
      [onSubmit, setIsExpanded],
    );

    const handleFocus = useCallback(() => {
      setIsExpanded(true);
    }, []);

    const handleBlur = useCallback((event: React.FocusEvent) => {
      // Only collapse if focus is leaving the entire form
      if (!event.currentTarget.contains(event.relatedTarget as Node)) {
        setIsExpanded(false);
      }
    }, []);

    // Handler to trigger form submission from textarea
    const handleSubmitRequest = useCallback(() => {
      if (formRef.current) {
        // Programmatically submit the form, which will trigger our handleSubmit
        formRef.current.requestSubmit();
      }
    }, []);

    // When noAnimation is true, always report collapsed state to children and CSS
    const shouldExpand = !noAnimation && isExpanded;

    const classes = classNames(
      "mobius-prompt-input",
      { "mobius-prompt-input--expanded": shouldExpand },
      className,
    );
    const enhancedChildren = Children.map(children, child => {
      if (isValidElement(child) && child.type === PromptInputTextarea) {
        return cloneElement(child, {
          isExpanded: shouldExpand,
          onSubmitRequest: handleSubmitRequest,
        } as unknown as PromptInputTextareaProps);
      }
      return child;
    });

    return (
      <div className="mobius-prompt-input__container">
        <form
          ref={node => {
            formRef.current = node;
            if (typeof ref === "function") {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          {...otherProps}
          className={classes}
          onSubmit={handleSubmit}
          onFocus={handleFocus}
          onBlur={handleBlur}
        >
          {enhancedChildren}
        </form>
        {showDisclaimer && (
          <Text variant="legal" className="mobius-prompt-input__disclaimer">
            This experience uses AI. BETA
          </Text>
        )}
      </div>
    );
  },
);

PromptInput.displayName = "PromptInput";
export { PromptInput };
